import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

/**
 * Credit Management Service
 * Handles all credit-related API calls
 */

export const creditService = {
  /**
   * Get credit requests
   * @param {string} accountId - Account ID (optional for Super Admin)
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @param {string} startDate - Start date (optional)
   * @param {string} endDate - End date (optional)
   * @param {string} userRole - User role
   * @param {Object} extraParams - Additional filters (for SUPERADMIN)
   * @param {string} [extraParams.search] - Search term
   * @param {string} [extraParams.status] - Status filter (e.g., 'PENDING')
   * @param {string} [extraParams.creditType] - Credit type (e.g., 'SHORTENING')
   * @param {string} [extraParams.sortBy] - Sort field (e.g., 'requestedAt')
   * @param {string} [extraParams.sortDirection] - Sort direction ('ASC' | 'DESC')
   * @returns {Promise<Object>} Credit requests with pagination
   */
  getCreditRequests: async (
    accountId,
    page = 0,
    size = 10,
    startDate = null,
    endDate = null,
    userRole = 'USER',
    extraParams = {}
  ) => {
    const endpoint = userRole === 'SUPERADMIN' 
      ? `${USER_URL}/api/credit-requests/all-requests`
      : `${USER_URL}/api/credit-requests/account/${accountId}`;

    // Build query params based on role
    const baseParams = { page, size };
    const dateParams = { startDate, endDate };

    // For SUPERADMIN, include new filters
    const superAdminParams = userRole === 'SUPERADMIN'
      ? {
          ...(extraParams.search !== undefined ? { search: extraParams.search } : {}),
          ...(extraParams.status !== undefined ? { status: extraParams.status } : {}),
          ...(extraParams.creditType !== undefined ? { creditType: extraParams.creditType } : {}),
          sortBy: extraParams.sortBy ?? 'requestedAt',
          sortDirection: extraParams.sortDirection ?? 'DESC',
          ...dateParams,
        }
      : undefined;

    // For ADMIN/USER, include status (optional) and creditType
    const accountParams = userRole !== 'SUPERADMIN'
      ? {
          ...(extraParams.status !== undefined ? { status: extraParams.status } : {}),
          ...(extraParams.creditType !== undefined ? { creditType: extraParams.creditType } : {}),
          ...dateParams,
        }
      : undefined;

    const response = await axios.get(endpoint, {
      params: { ...baseParams, ...(superAdminParams || accountParams || {}) },
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });

    // API returns { success, message, data: { ...pagination, content: [...] } }
    const data = response?.data?.data;
    return (
      data || {
        content: [],
        page,
        size,
        totalPages: 0,
        totalRecords: 0,
        numberOfElements: 0,
      }
    );
  },

  /**
   * Create credit request
   * @param {Object} requestData - Credit request data
   * @returns {Promise<Object>} Created credit request
   */
  createCreditRequest: async (requestData) => {
    const response = await axios.post(`${USER_URL}/api/credit-requests/create`, requestData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Update credit request status
   * @param {string} requestId - Request ID
   * @param {Object} statusData - Status update data
   * @returns {Promise<Object>} Updated credit request
   */
  updateCreditRequestStatus: async (requestId, statusData) => {
    const response = await axios.post(`${USER_URL}/api/credit-requests/update-status/${requestId}`, statusData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Create credits
   * @param {Object} creditData - Credit data
   * @returns {Promise<Object>} Created credit data
   */
  createCredits: async (creditData) => {
    const response = await axios.post(`${USER_URL}/api/credits/create`, creditData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Update credits
   * @param {Object} creditData - Credit update data
   * @returns {Promise<Object>} Updated credit data
   */
  updateCredits: async (creditData) => {
    const response = await axios.put(`${USER_URL}/api/credits/update`, creditData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get credits by account ID
   * @param {string} accountId - Account ID
   * @returns {Promise<Object>} Credits data
   */
  getCreditsByAccount: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/credits/account/${accountId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Get credit history by account ID
   * @param {string} accountId - Account ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} Credit history data
   */
  getCreditHistoryByAccount: async (accountId, params = {}) => {
    // Add exceptConsumption=true parameter to exclude consumption records
    const queryParams = {
      exceptConsumption: true,
      ...params
    };
    
    const response = await axios.get(`${USER_URL}/api/credits/history/account/${accountId}`, {
      params: queryParams,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Delete credit
   * @param {string} creditId - Credit ID
   * @returns {Promise<Object>} Delete response
   */
  deleteCredit: async (creditId) => {
    const response = await axios.delete(`${USER_URL}/api/credits/delete/${creditId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Update credit status
   * @param {Object} statusData - Status update data
   * @returns {Promise<Object>} Updated credit status
   */
  updateCreditStatus: async (statusData) => {
    const response = await axios.put(`${USER_URL}/api/credits/update-status`, statusData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Search credit history
   * @param {Object} searchData - Search parameters
   * @returns {Promise<Object>} Credit history search results
   */
  searchCreditHistory: async (searchData) => {
    const response = await axios.post(`${USER_URL}/api/credits/history/search`, searchData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Export credit history
   * @param {Object} exportData - Export parameters
   * @returns {Promise<Blob>} Export file blob
   */
  exportCreditHistory: async (exportData) => {
    const response = await axios({
      method: "post",
      url: `${USER_URL}/api/credits/history/export`,
      data: exportData,
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` },
      responseType: "blob"
    });
    return response.data;
  },

  /**
   * Approve credit request
   * @param {string} requestId - Request ID
   * @returns {Promise<Object>} Approval response
   */
  approveCreditRequest: async (requestId, approvedBy) => {
    const response = await axios.post(
      `${USER_URL}/api/credit-requests/${requestId}/approve`,
      null,
      {
        params: { approvedBy },
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
      }
    );
    return response.data;
  },

  /**
   * Reject credit request
   * @param {string} requestId - Request ID
   * @param {string} remarks - Rejection remarks
   * @returns {Promise<Object>} Rejection response
   */
  rejectCreditRequest: async (requestId, remarks, approvedBy) => {
    const response = await axios.post(
      `${USER_URL}/api/credit-requests/${requestId}/reject`,
      null,
      {
        params: { approvedBy, remarks },
        headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
      }
    );
    return response.data;
  }
};

export default creditService;
